import React, { useState, useContext, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Grid,
  Stack,
  Typography,
  Box,
  Fade,
  alpha,
  useTheme,
  Paper,
  IconButton,
  Chip,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  TextField,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import HomeTwoToneIcon from '@mui/icons-material/HomeTwoTone';
import WifiIcon from '@mui/icons-material/Wifi';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { AuthContext } from '../../AuthContext';
import { useApi } from '../../hooks/useApi';
import UnifiCheckDetailDrawer from './UnifiCheckDetailDrawer';
import { pickLatestForSite } from './unifiAuditRecords';

// ═══════════════════════════════════════════════════════════════════════════
// Check registries - one entry per checkKey per producer, each with a human label and a
// describe(detail) function that renders one details[] row as a plain-language string.
// Field shapes are the ground truth from the actual producers:
//   cma-scripts/cma/audit/producers/unifi/Update-UnifiAudit.ps1        (config drift / "Audit")
//   cma-scripts/cma/audit/producers/unifi-perf/Update-UnifiPerfAudit.ps1     ("Performance")
//   cma-scripts/cma/audit/producers/unifi-client/Update-UnifiClientAudit.ps1 ("Client")
// ═══════════════════════════════════════════════════════════════════════════

const AUDIT_CHECKS = {
  ssidsManualMode: { label: 'SSIDs set to Manual mode', description: "Whether each SSID's setting preference is Manual rather than Auto (HostiFi guidance) - distinct from the per-SSID minimum data rate preference below.", describe: (d) => `Setting preference is "${d.value}" (should be manual)` },
  bssTransitionEnabled: { label: 'BSS Transition (802.11v) enabled', description: '802.11v BSS Transition is expected on for every SSID per the CMA standard Wi-Fi policy.', describe: (d) => `BSS Transition is ${d.value ? 'on' : 'off'}` },
  minrateAuto: { label: 'Minimum data rate set to Auto', description: "Whether each SSID's minimum data rate preference is Auto (the controller's own adaptive floor) rather than a manually pinned rate.", describe: (d) => `Minimum data rate preference is "${d.value}" (should be auto)` },
  fastRoamingDisabled: { label: 'Fast Roaming (802.11r) matches policy', description: '802.11r Fast Roaming is off by default; a site may enable it per-SSID where voice/mobile roaming matters, but policy expects it consistently either on or off across all SSIDs.', describe: () => 'Fast Roaming is enabled (policy default is disabled)' },
  securityStrong: { label: 'Strong security (WPA2+)', description: 'Whether every SSID uses WPA2 or better - open, WEP, and WPA1 all fail.', describe: (d) => `Security: ${d.security}${d.wpaMode ? ` / WPA mode: ${d.wpaMode}` : ''}` },
  guestNetworkExists: { label: 'Guest network exists', description: 'Whether a guest SSID is configured for the site, per CMA standard policy.', describe: () => 'No guest network configured' },
  guestNetworkSegregated: { label: 'Guest network segregated from corporate', description: 'Whether the guest network(s) use a separate network config from corporate rather than sharing one.', describe: (d) => `Shares network config ${d.networkconfId} with corporate` },
  guestDhcpPoolAdequate: { label: 'Guest DHCP pool adequate size', description: "Whether each guest network's DHCP pool has at least 40 addresses.", describe: (d) => `Pool size ${d.poolSize} (${d.dhcpStart} - ${d.dhcpStop})` },
  minRssiDisabled: { label: 'Minimum RSSI matches policy', description: 'Minimum RSSI (sticky-client kick) is left off by default unless a site has explicitly opted in - legacy HostiFi guidance.', describe: (d) => `Min-RSSI enabled on radios: ${d.radios}` },
  transmitPowerCorrect: { label: 'Transmit power staged correctly (2.4GHz < 5GHz)', description: "Whether each AP's 2.4GHz transmit power is lower than its 5GHz power, to encourage client association on 5GHz.", describe: (d) => `2.4GHz ${d.tx2g} dBm >= 5GHz ${d.tx5g} dBm` },
  channelWidthsCorrect: { label: 'Channel widths correct', description: "Whether each AP's channel widths match policy (2.4GHz = 20MHz, 5GHz = 40 or 80MHz).", describe: (d) => (d.issues || []).join(', ') },
  channelsAuto: { label: 'Channels set to Auto', description: "Whether each AP's radios use Auto channel selection rather than a manually pinned channel.", describe: (d) => `Manually pinned: ${d.channels}` },
  meshingDisabled: { label: 'Meshing disabled on wired-uplink APs', description: 'Mesh uplink should be disabled on APs whose uplink is actually wired - meshing on a wired AP wastes airtime for no benefit.', describe: () => 'Mesh VAP enabled on a wired-uplink AP (wasted airtime)' },
  networksNotAutoScale: { label: 'Networks not set to Auto-Scale', description: 'Auto-Scale should be left off on each network per CMA standard policy.', describe: () => 'Auto-Scale is enabled on this network' },
  apInventory: { label: 'AP inventory (informational)', description: 'Informational AP count and model breakdown for the site - never scored (NODATA / NOOP, like SonicWALL deviceOnline).', describe: (d) => `${d.count} AP(s): ${Object.entries(d.models || {}).map(([m, c]) => `${m} x${c}`).join(', ')}` },
};
const AUDIT_ORDER = Object.keys(AUDIT_CHECKS);

const PERF_CHECKS = {
  radioSatisfaction: { label: 'Radio Satisfaction', description: "Flags active radios (excluding idle ones) whose controller-reported satisfaction score falls below the good (>=90) or marginal (80-89) thresholds, with a trend vs. this radio's own 14-day baseline.", describe: (d) => `Satisfaction ${d.satisfaction}${d.baseline != null ? ` (baseline ${Math.round(d.baseline)})` : ''} - ${d.trend}${d.numSta != null ? ` - ${d.numSta} client(s)` : ''}` },
  radioUtilization: { label: 'Channel Utilization', description: 'Flags active radios whose airtime utilization exceeds the marginal/problem/red thresholds, distinguishing self-generated load from neighbor/co-channel interference.', describe: (d) => `Utilization ${d.utilization}% (${d.likelyCause})${d.baseline != null ? ` - baseline ${Math.round(d.baseline)}%` : ''} - ${d.trend}` },
  radioRetriesTrend: { label: 'Retry Rate Trend', description: "Flags radios whose hourly retry RATE (a diff between consecutive telemetry snapshots, not the raw lifetime tx_retries counter) climbs at or above both an absolute floor and 2x their own 14-day baseline rate - avoids flagging a trivial absolute wobble on a still-small counter as a big percentage swing.", describe: (d) => `${d.currentRate} retries/hour vs. this radio's own baseline ${Math.round(d.baselineRate)}/hour` },
  apUplinkHealth: { label: 'AP Uplink Health', description: 'Flags APs whose wired uplink speed is below 1000 Mbps or running half-duplex - a wired-layer bottleneck regardless of RF quality.', describe: (d) => `Uplink ${d.uplinkSpeed ?? 'unknown'} Mbps${d.fullDuplex === false ? ' (half-duplex)' : ''}` },
};
const PERF_ORDER = Object.keys(PERF_CHECKS);

const CLIENT_CHECKS = {
  clientRoaming: { label: 'Roaming / Sticky Clients', description: 'Classifies weak-signal clients (<=-78 dBm) as a healthy roam, a bounce loop (reconnects to the same AP still weak), or sticky (stays weak, never roams).', describe: (d) => `${d.classification}${d.repeated ? ' (repeated)' : ''} at AP ${d.apMac ?? 'unknown'}, signal ${d.signal} dBm` },
  clientChronicSatisfaction: { label: 'Chronic Low Satisfaction', description: 'Flags clients whose satisfaction score has tripped the marginal/problem threshold in at least 3 of the last 4 hourly snapshots - a sustained rather than momentary issue.', describe: (d) => `Satisfaction ${d.satisfaction} - tripped ${d.trippedCount} of last ${d.ofLastN} hourly snapshots` },
  clientWeakSignal: { label: 'Persistent Weak Signal / SNR', description: 'Flags clients with persistently poor signal (RSSI) or SNR across at least 3 of the last 4 hourly snapshots.', describe: (d) => `Signal ${d.signal} dBm, SNR ${d.snr} dB - tripped ${d.trippedCount} of last ${d.ofLastN} hourly snapshots` },
  clientDisconnectChurn: {
    label: 'Retries / Anomalies / Legacy Rate',
    description: "Flags clients with an hourly retry RATE (normalized by actual elapsed time between telemetry snapshots, not the raw lifetime tx_retries counter) at or above both an absolute floor and 2x their own baseline rate, non-zero controller-reported anomalies, or a legacy (<=12 Mbps) transmit rate. Does not track reconnects - a re-association also fires on a healthy roam to a different AP, which clientRoaming (Roaming / Sticky Clients) already classifies correctly.",
    describe: (d) => {
      const parts = [(d.reasons || []).join(', ')];
      if (d.txRate != null) parts.push(`tx_rate ${Math.round(d.txRate / 1000)} Mbps`);
      if (d.retriesCurrentRate != null) parts.push(`retries ${d.retriesCurrentRate}/hour${d.retriesBaselineRate != null ? ` vs. baseline ${Math.round(d.retriesBaselineRate)}/hour` : ''}`);
      if (d.anomalies) parts.push(`anomalies ${d.anomalies}`);
      return parts.join(' - ');
    },
  },
};
const CLIENT_ORDER = Object.keys(CLIENT_CHECKS);

const SECTIONS = [
  { key: 'audit', label: 'Audit', auditRunId: 'UNIFI', checks: AUDIT_CHECKS, order: AUDIT_ORDER },
  { key: 'perf', label: 'Performance', auditRunId: 'UNIFIPERF', checks: PERF_CHECKS, order: PERF_ORDER },
  { key: 'client', label: 'Client', auditRunId: 'UNIFICLIENT', checks: CLIENT_CHECKS, order: CLIENT_ORDER },
];

function summarizeSection(record, order) {
  const checks = record?.jobResults?.checks || {};
  let failing = 0, warning = 0, passed = 0, scored = 0;
  order.forEach((key) => {
    const c = checks[key];
    if (!c) return;
    const r = ('' + c.result).toUpperCase();
    if (r === 'NODATA' || r === 'NOOP') return; // informational, never scored
    scored++;
    if (r === 'FAIL') failing++;
    else if (r === 'WARN' || r === 'WARNING') warning++;
    else if (r === 'PASS') passed++;
  });
  const pct = scored > 0 ? Math.round((passed / scored) * 100) : null;
  return { failing, warning, passed, scored, pct };
}

function StatusIcon({ result }) {
  const theme = useTheme();
  const r = ('' + result).toUpperCase();
  if (r === 'PASS') return <CheckCircleIcon sx={{ color: theme.palette.success.main, fontSize: 20 }} />;
  if (r === 'WARN' || r === 'WARNING') return <WarningAmberIcon sx={{ color: theme.palette.warning.main, fontSize: 20 }} />;
  if (r === 'FAIL') return <ErrorIcon sx={{ color: theme.palette.error.main, fontSize: 20 }} />;
  return <HourglassEmptyIcon sx={{ color: theme.palette.text.disabled, fontSize: 20 }} />;
}

function UnifiHealth() {
  const { user } = useContext(AuthContext);
  const theme = useTheme();
  const jApi = useApi();

  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedSite, setSelectedSite] = useState(null);
  const [records, setRecords] = useState({ audit: null, perf: null, client: null });
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(true);
  const [isLoadingRecords, setIsLoadingRecords] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [detailDrawer, setDetailDrawer] = useState({ open: false, checkKey: null, check: null, meta: null, auditRunId: null });

  // Accounts with at least one UniFi site - the only ones relevant here.
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        setIsLoadingAccounts(true);
        const response = await jApi.request('GET', 'cma/account');
        const withUnifi = (response.data || [])
          .filter(a => Array.isArray(a.unifi_id) && a.unifi_id.length > 0)
          .sort((a, b) => a.code.localeCompare(b.code));
        setAccounts(withUnifi);

        const savedCode = sessionStorage.getItem('unifiHealthAccount');
        const saved = withUnifi.find(a => a.code === savedCode);
        setSelectedAccount(saved || withUnifi[0] || null);
      } catch (ex) {
        setErrorMessage(ex.toString());
      } finally {
        setIsLoadingAccounts(false);
      }
    };
    fetchAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Site follows the selected account's unifi_id list.
  useEffect(() => {
    if (!selectedAccount) {
      setSelectedSite(null);
      return;
    }
    setSelectedSite(selectedAccount.unifi_id[0]);
  }, [selectedAccount]);

  const fetchRecords = async () => {
    if (!selectedAccount || !selectedSite) return;
    try {
      setErrorMessage('');
      setIsLoadingRecords(true);
      const responses = await Promise.all(
        SECTIONS.map(s => jApi.request('GET', `cma/audit?account=${selectedAccount.code}&auditRunId=${s.auditRunId}`))
      );
      // Facts are one-per-day (source|site|date); API may return several days.
      // Always show the latest auditDate for this site, not the first array hit.
      const bySite = responses.map(r => pickLatestForSite(Array.isArray(r.data) ? r.data : [], selectedSite));
      setRecords({ audit: bySite[0], perf: bySite[1], client: bySite[2] });
    } catch (ex) {
      console.error('Failed to fetch Unifi health data:', ex);
      setErrorMessage(ex.toString());
      setRecords({ audit: null, perf: null, client: null });
    } finally {
      setIsLoadingRecords(false);
    }
  };

  useEffect(() => {
    fetchRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount, selectedSite]);

  const handleAccountChange = (event, newValue) => {
    setSelectedAccount(newValue);
    if (newValue) sessionStorage.setItem('unifiHealthAccount', newValue.code);
  };

  const openDetail = (section, checkKey, check) => {
    setDetailDrawer({ open: true, checkKey, check, meta: section.checks[checkKey], auditRunId: section.auditRunId });
  };

  const siteOptions = selectedAccount?.unifi_id || [];

  const headerGradient = {
    background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
    borderRadius: 3,
    border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.1 : 0.2)}`,
    position: 'relative',
    overflow: 'hidden',
    boxShadow: theme.palette.mode === 'dark'
      ? `0 4px 12px ${alpha(theme.palette.common.black, 0.3)}`
      : `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
  };

  const isLoading = isLoadingAccounts || isLoadingRecords;

  return (
    <Box sx={{ minHeight: '100vh', pb: 4 }}>
      <Container maxWidth="xl">
        <Fade in timeout={500}>
          <Box sx={{ pt: 3, pb: 2 }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderRadius: 2, background: alpha(theme.palette.background.paper, 0.6), backdropFilter: 'blur(10px)', border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
              <Link to="/home" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, borderRadius: 1.5, '&:hover': { background: alpha(theme.palette.secondary.main, 0.1) } }}>
                  <HomeTwoToneIcon sx={{ fontSize: 20, color: theme.palette.secondary.main }} />
                  <Typography variant="body2" sx={{ color: theme.palette.secondary.main, fontWeight: 500 }}>Home</Typography>
                </Box>
              </Link>
              <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>/</Typography>
              <Link to="/operation" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, px: 1, py: 0.5, borderRadius: 1.5, '&:hover': { background: alpha(theme.palette.secondary.main, 0.1), color: theme.palette.secondary.main } }}>
                  Operation Center
                </Typography>
              </Link>
              <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>/</Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 600, px: 1, py: 0.5 }}>Unifi Health</Typography>
            </Box>
          </Box>
        </Fade>

        <Fade in timeout={600}>
          <Box sx={{ ...headerGradient, p: 4, mb: 4 }}>
            <Grid container spacing={3} alignItems="center">
              <Grid size={{ xs: 12, md: 6 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box sx={{ width: 72, height: 72, borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.3)} 0%, ${alpha(theme.palette.info.main, 0.1)} 100%)`, boxShadow: `0 8px 16px ${alpha(theme.palette.info.main, 0.3)}`, border: `2px solid ${alpha(theme.palette.info.main, 0.3)}` }}>
                    <WifiIcon sx={{ fontSize: 40, color: 'info.main' }} />
                  </Box>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.primary.main} 100%)`, backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                      Unifi Health
                    </Typography>
                    <Typography variant="body1" color="text.secondary">Audit, Performance &amp; Client - one site at a time</Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" justifyContent="flex-end">
                  <Autocomplete
                    options={accounts}
                    getOptionLabel={(a) => `${a.name} (${a.code})`}
                    value={selectedAccount}
                    onChange={handleAccountChange}
                    isOptionEqualToValue={(a, b) => a.code === b.code}
                    size="small"
                    sx={{ width: { xs: '100%', sm: 260 }, background: theme.palette.background.paper, borderRadius: 1 }}
                    renderInput={(params) => <TextField {...params} label="Account" />}
                  />
                  {siteOptions.length > 1 && (
                    <ToggleButtonGroup value={selectedSite} exclusive onChange={(e, v) => v && setSelectedSite(v)} size="small">
                      {siteOptions.map(s => (
                        <ToggleButton key={s} value={s} sx={{ fontFamily: 'monospace' }}>{s}</ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  )}
                  {siteOptions.length === 1 && (
                    <Chip label={siteOptions[0]} sx={{ fontFamily: 'monospace', fontWeight: 600 }} />
                  )}
                  <IconButton onClick={fetchRecords} disabled={isLoading} sx={{ background: alpha(theme.palette.primary.main, 0.1), '&:hover': { background: alpha(theme.palette.primary.main, 0.2) } }}>
                    <RefreshIcon />
                  </IconButton>
                </Stack>
              </Grid>
            </Grid>
            {isLoading && <LinearProgress sx={{ position: 'absolute', bottom: 0, left: 0, right: 0 }} />}
          </Box>
        </Fade>

        {errorMessage && (
          <Fade in>
            <Paper sx={{ p: 3, mb: 3, background: alpha(theme.palette.error.main, 0.1), border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`, borderRadius: 2 }}>
              <Typography variant="body1" color="error.main" sx={{ fontWeight: 500 }}>{errorMessage}</Typography>
            </Paper>
          </Fade>
        )}

        {!selectedAccount && !isLoadingAccounts && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">No accounts with a UniFi site found.</Typography>
          </Paper>
        )}

        {selectedAccount && (
          <Stack spacing={2}>
            {SECTIONS.map((section, idx) => {
              const record = records[section.key];
              const summary = summarizeSection(record, section.order);
              const checks = record?.jobResults?.checks || {};

              return (
                <Fade in timeout={700 + idx * 100} key={section.key}>
                  <Accordion defaultExpanded={idx === 0} disableGutters sx={{ '&:before': { display: 'none' }, border: `1px solid ${alpha(theme.palette.divider, 0.4)}`, borderRadius: 2, overflow: 'hidden' }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: '100%', pr: 1 }}>
                        <Typography sx={{ fontWeight: 700, flex: 1 }}>{section.label}</Typography>
                        {record ? (
                          <>
                            {summary.failing > 0
                              ? <Chip size="small" color="error" variant="outlined" label={`${summary.failing} failing`} />
                              : <Chip size="small" color="success" variant="outlined" label="0 failing" />}
                            {summary.warning > 0 && <Chip size="small" color="warning" variant="outlined" label={`${summary.warning} warning`} />}
                            <Chip
                              size="small"
                              variant="outlined"
                              label={summary.pct == null ? 'No data' : `${summary.pct}%`}
                              color={summary.pct == null ? 'default' : summary.pct >= 90 ? 'success' : summary.pct >= 70 ? 'warning' : 'error'}
                            />
                          </>
                        ) : (
                          <Chip size="small" variant="outlined" label="No data yet" />
                        )}
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      {!record ? (
                        <Box sx={{ p: 3, textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            No {section.label} data for this site yet - has Update-Unifi{section.key === 'audit' ? '' : section.key === 'perf' ? 'Perf' : 'Client'}Audit run for it?
                          </Typography>
                        </Box>
                      ) : (
                        section.order.map((checkKey, rowIdx) => {
                          const check = checks[checkKey];
                          if (!check) return null;
                          const meta = section.checks[checkKey];
                          return (
                            <React.Fragment key={checkKey}>
                              {rowIdx > 0 && <Divider />}
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1.5}
                                sx={{ px: 2, py: 1.25, cursor: 'pointer', '&:hover': { background: alpha(theme.palette.action.hover, 0.5) } }}
                                onClick={() => openDetail(section, checkKey, check)}
                              >
                                <StatusIcon result={check.result} />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{meta.label}</Typography>
                                  <Typography variant="caption" color="text.secondary">{check.message}</Typography>
                                </Box>
                                {check.details?.length > 0 && (
                                  <Chip size="small" variant="outlined" label={`${check.details.length} row${check.details.length !== 1 ? 's' : ''}`} />
                                )}
                              </Stack>
                            </React.Fragment>
                          );
                        })
                      )}
                    </AccordionDetails>
                  </Accordion>
                </Fade>
              );
            })}
          </Stack>
        )}
      </Container>

      <UnifiCheckDetailDrawer
        open={detailDrawer.open}
        onClose={() => setDetailDrawer(prev => ({ ...prev, open: false }))}
        checkKey={detailDrawer.checkKey}
        check={detailDrawer.check}
        meta={detailDrawer.meta}
        auditRunId={detailDrawer.auditRunId}
      />
    </Box>
  );
}

export default UnifiHealth;
